import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { DotsThree as DotsThreeIcon } from "@phosphor-icons/react/DotsThree";
import { Eye as EyeIcon } from "@phosphor-icons/react/Eye";
import { Lock as LockIcon } from "@phosphor-icons/react/Lock";
import { paths } from "config/paths";
import Link from "next/link";
import * as React from "react";
import { displayBytes, getDatasetThumbnailUrl } from "utils";
import { useWebRouter } from "~utils/next-router/use-web-router";

import { usePopover } from "~ui-components/hooks/use-popover";
import { Dataset as DefaultDataset } from "~ui-components/types/__generated/gql/graphql";

import { DataTypeIcon } from "./data-type-icon";
import { ItemIcon } from "./item-icon";
import { ItemMenu } from "./item-menu";
import { getDatasetMaps } from "./item-row";

export interface ItemCardProps<T extends DefaultDataset = DefaultDataset> {
	item: T;
	onDelete?: (itemId: string) => void;
	size?: "medium" | "large";
	isDeleteDataAllowed: boolean;
}
export function ItemCard<T extends DefaultDataset = DefaultDataset>({
	item,
	onDelete,
	size = "medium",
	isDeleteDataAllowed,
}: ItemCardProps<T>) {
	const popover = usePopover<HTMLButtonElement>();
	const router = useWebRouter();

	const handleDelete = React.useCallback(() => {
		popover.handleClose();
		onDelete?.(item.id);
	}, [item, popover, onDelete]);

	return (
		<React.Fragment>
			<Card
				key={item.id}
				sx={{
					transition: "box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
					"&:hover": { boxShadow: "var(--mui-shadows-16)" },
				}}>
				<Stack
					direction="row"
					spacing={3}
					sx={{
						alignItems: "center",
						justifyContent: "space-between",
						pt: 2,
						px: 2,
					}}>
					<Stack
						direction="row"
						spacing={1}>
						<SvgIcon>{item?.isPublic ? <EyeIcon /> : <LockIcon />}</SvgIcon>
						<SvgIcon>{DataTypeIcon[item.type]}</SvgIcon>
					</Stack>
					{isDeleteDataAllowed && (
						<IconButton
							onClick={popover.handleOpen}
							ref={popover.anchorRef}>
							<DotsThreeIcon weight="bold" />
						</IconButton>
					)}
				</Stack>
				<Stack
					spacing={1}
					padding={2}
					sx={{
						color: "unset",
						textDecoration: "none",
					}}
					component={Link}
					href={paths.accountName.data.datasetId.root(
						router._query.accountName,
						item.id,
					)}>
					<Box display="flex">
						<ItemIcon
							size={size}
							src={getDatasetThumbnailUrl(item.id, item.updatedAt)}
						/>
					</Box>
					<div>
						<Typography variant="subtitle2">{item.name}</Typography>
						<Stack
							direction="row"
							spacing={1}
							sx={{ alignItems: "center", justifyContent: "space-between" }}>
							<Typography
								color="text.secondary"
								variant="caption">
								{displayBytes(item.size)} • Used in{" "}
								{getDatasetMaps(item).length} maps
							</Typography>
						</Stack>
					</div>
				</Stack>
			</Card>
			{isDeleteDataAllowed && (
				<ItemMenu
					anchorEl={popover.anchorRef.current}
					onClose={popover.handleClose}
					onDelete={handleDelete}
					open={popover.open}
				/>
			)}
		</React.Fragment>
	);
}
